import AIIcon from 'assets/svg/icons/AIIcon';
import { Button } from 'components/atoms/Button';
import { Modal, type ModalApi } from 'components/atoms/Modal';
import useAIUsageConsent from 'hooks/shared/useAIUsageConsent';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { AIToolConsent, AIV3UsageConsent } from 'types/users.types';
import {
  AIFeatureUsageConsentEnum,
  AIToolUsageConsentEnum,
} from 'types/users.types';
import AIToolConsentList from './AIToolConsentList';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_CHOOSECONSENTS,
  WEB_AIASSISTANT_UPDATEDCONSENTS,
} from 'utils/amplitude/events/ai.amplitude';

type AIToolConsentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (consents: AIV3UsageConsent) => void;
};

const AIToolConsentModal: FC<AIToolConsentModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { consents, patchConsents, isLoading } = useAIUsageConsent();
  const apiRef = useRef<ModalApi>(null);

  const [localConsents, setLocalConsents] = useState<AIToolConsent>({
    expenses: consents?.expenses || AIToolUsageConsentEnum.ask,
    revenues: consents?.revenues || AIToolUsageConsentEnum.ask,
    taxes: consents?.taxes || AIToolUsageConsentEnum.ask,
    summaries: consents?.summaries || AIToolUsageConsentEnum.ask,
  });

  // Handle modal open/close based on isOpen prop
  useEffect(() => {
    if (isOpen) {
      logAmplitude(WEB_AIASSISTANT_CHOOSECONSENTS);
      apiRef.current?.openModal();
    } else {
      apiRef.current?.closeModal();
    }
  }, [isOpen]);

  const handleToolConsentChange = useCallback(
    (consents: AIToolConsent) => setLocalConsents(consents),
    [],
  );

  const handleSave = useCallback(async () => {
    await patchConsents({
      ...localConsents,
      ai_tax_advisor: AIFeatureUsageConsentEnum.allow,
    });
    onSave?.(localConsents as AIV3UsageConsent);

    logAmplitude(WEB_AIASSISTANT_UPDATEDCONSENTS, { ...localConsents });

    onClose();
  }, [localConsents, patchConsents, onSave, onClose]);

  return (
    <Modal
      name="AI Tool Consent Modal"
      width="2xl"
      apiRef={apiRef}
      color="primary"
      onClose={onClose}
      render={() => (
        <div className="p-6 flex flex-col">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-taxes-200 self-center mb-4">
            <AIIcon className={'w-8 h-8 text-taxes'} />
          </div>

          <h2 className="text-xl font-semibold text-center mb-2">
            {t('ai_bot.tool_consent.title')}
          </h2>

          <p className="text-sm text-gray-600 text-center mb-6">
            {t('ai_bot.tool_consent.description')}
          </p>

          <div className="rounded-lg border border-primary-100">
            <AIToolConsentList
              consents={localConsents}
              onChange={handleToolConsentChange}
              className="!px-0"
              itemClassName="px-4"
            />
          </div>

          <div className="flex space-x-4 mt-6 pt-4 border-t">
            <Button
              structure="secondary"
              color="primary"
              className="flex-1"
              onClick={onClose}
              disabled={isLoading}
            >
              {t('ai_bot.tool_consent.cancel')}
            </Button>
            <Button
              color="taxes"
              className="flex-1"
              onClick={handleSave}
              loading={isLoading}
              disabled={isLoading}
            >
              {t('ai_bot.tool_consent.save')}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default AIToolConsentModal;
